import type { PresetV2 } from "./mockData";
import { RUBRO_HOGAR_DECO } from "../../lib/rubro";

// Feature flag del modo "Video con influencer". Mientras esté en `false` el
// modo queda OCULTO para TODOS — tanto en el design-row de la home V2 como
// en la pantalla de Modos. Con `true` es visible para todos los planes
// (ADR-561): la restricción Pro/Ultra aplica al GENERAR
// (VideoPlanRestrictionModal vía checkGenerationGuards).
export const VIDEO_INFLUENCER_ENABLED = true;

// Los modos "Catálogo de muebles" (ADR-582) e "Infografías" (ADR-814) YA NO
// tienen feature flag: se habilitaron y ahora se gatean por el rubro de la
// MARCA activa — ver `soloHogarDeco` acá abajo y
// ADR-2026-07-31-modos-muebles-por-rubro-de-marca.
//
// La rama del rediseño había sacado `INFOGRAFIA_ENABLED` por su cuenta, con la MISMA
// regla de rubro pero sólo para Infografía y dejando `CATALOGO_MUEBLES_ENABLED` en
// `false`. Al mergear gana este sistema, que es más completo: cubre los dos modos y
// además esconde los que NO aplican a muebles (`ocultoEnHogarDeco`) —
// ADR-2026-08-05-un-solo-gate-de-rubro-para-los-modos.

// Catálogo de "modos" de generación de Interfaz V2 (los mismos que aparecen en
// la home V2 / el design-row). Centralizado acá para reusarlo en la pantalla
// de Modos.
//
// Cada modo incluye el objeto `preset` EXACTO que usa InterfazV2 para abrir el
// sheet del modo (mismo id/kind/imágenes que las cards sintéticas de la home).
// Se pasa como `state.presetData` al navegar para que InterfazV2 monte el sheet
// en el primer paint (mecanismo `initialNavPreset`) y no se vea un flash de la
// home V2.

export interface ModeCard {
  /** Segmento de ruta del modo en V2: `/interfaz-v2/modo/<path>`. */
  path: string;
  /** Carátula en video (mp4 corto, autoplay loop muteado). Opcional: los modos
   *  sin video propio (modelo, catálogo-muebles, infografía) muestran
   *  `preset.imageUrl` estático en su lugar (los consumidores caen a `<img>` si
   *  no hay video). */
  video?: string;
  /** Portada estática del modo. Con `video`, es el poster mientras carga; SIN
   *  `video` sigue siendo la portada que consume la tira del inicio
   *  (`useModosGenerar`: `arte?.poster || m.poster || m.preset.imageUrl`). */
  poster?: string;
  /** Preset que abre el modo en V2 (espeja las cards del design-row). */
  preset: PresetV2;
  /** Si true, el modo solo se muestra a dev testers (los consumidores de
   *  `MODES` deben filtrar con `isDevTester`). */
  devOnly?: boolean;
  /**
   * Si true, el modo NO aparece en ninguna lista de descubrimiento (`/modos`, la tira
   * de la home, el sheet de Generar de mobile, el drawer de Mi marca, "Todas las
   * herramientas" de la sidebar) pero SIGUE VIVO en el catálogo —
   * ADR-2026-08-20-anuncio-sale-de-las-listas-sin-salir-del-catalogo.
   *
   * Es un flag y no un borrado de `MODES` a propósito: la entrada la consumen cosas
   * que NO son listas y que tienen que seguir funcionando —`MODE_PATH_TO_KIND` /
   * `MODE_KIND_TO_PATH`, `PORTADA_CUADRADA`, `anuncioTercero()`, y los
   * `MODES.find(m => m.path === 'anuncio')` de `GeneratedImagesInline` y
   * `GeneratedImagesHistory` que arman el presetData del atajo desde una imagen
   * abierta—. Borrar la entrada rompía justo los accesos que se querían conservar.
   *
   * Lo respeta `modosVisibles()`; el deep-link `/interfaz-v2/modo/<path>` no lo mira.
   */
  ocultoEnListas?: boolean;
  /** Si true, el modo solo se muestra cuando la MARCA activa es del rubro
   *  "Hogar y Decoración" (los consumidores de `MODES` deben filtrar con
   *  `esRubroHogarDeco(rubro de useRubroMarcaActiva)`). Reemplaza a los viejos
   *  flags CATALOGO_MUEBLES_ENABLED / INFOGRAFIA_ENABLED —
   *  ADR-2026-07-31-modos-muebles-por-rubro-de-marca. */
  soloHogarDeco?: boolean;
  /** Inverso de `soloHogarDeco`: el modo se OCULTA cuando la marca activa es de
   *  "Hogar y Decoración". Para los modos que ponen el producto sobre un modelo
   *  IA, que no aplican a un mueble —
   *  ADR-2026-07-31-hogar-deco-sin-plantillas-ni-modelo. */
  ocultoEnHogarDeco?: boolean;
  /** Ruta dedicada FUERA de InterfazV2. Si está presente, los consumidores del catálogo (Modos,
   *  Estilos/contenidoAtlas) navegan ACÁ en vez de a `/interfaz-v2/modo/<path>` — InterfazV2 no monta
   *  este modo porque tiene pantalla propia. Ej: Shorts Studio (`/shorts-studio`). Centraliza el
   *  "route requirement" en el catálogo en vez de un hardcode por cada consumidor. */
  route?: string;
}

// Orden igual al design-row de la home V2: [auto, modelo, catálogo,
// video-influencer, asistente, anuncio].
export const MODES: ModeCard[] = [
  {
    // Modo "Shorts Studio" — pantalla propia (ruta /shorts-studio); Modos.tsx intercepta
    // este path y navega ahí en vez de a InterfazV2. Carátula PLACEHOLDER (gradiente) hasta
    // tener un preview real generado.
    path: "shorts-studio",
    route: "/shorts-studio",
    // Sólo dev testers — ADR-2026-08-20-shorts-studio-vuelve-a-interno. El modo estaba
    // listado para todos con carátula PLACEHOLDER (el gradiente de acá abajo). Con el
    // flag sale de `/modos`, del sheet de Generar, del drawer de Mi marca y de la
    // sidebar; la ruta `/shorts-studio` sigue accesible por URL para poder probarlo.
    //
    // Efecto colateral buscado: en el sheet y en el drawer este modo era un CLICK
    // MUERTO —los dos arman `/interfaz-v2/modo/shorts-studio`, que `InterfazV2` no
    // resuelve porque el modo tiene pantalla propia—, así que sacarlo de esas dos
    // listas apaga un bug que estaba vivo en producción.
    devOnly: true,
    preset: {
      id: "shorts-studio-card",
      title: "Shorts Studio",
      description:
        "Subí tu video hablando a cámara y transformalo por completo en un estilo: claymation, cómic, acuarela y más.",
      imageUrl:
        "data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='400'%20height='300'%3E%3Cdefs%3E%3ClinearGradient%20id='g'%20x1='0'%20y1='0'%20x2='1'%20y2='1'%3E%3Cstop%20offset='0'%20stop-color='%23d946ef'/%3E%3Cstop%20offset='1'%20stop-color='%237c3aed'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect%20width='400'%20height='300'%20fill='url(%23g)'/%3E%3Ctext%20x='200'%20y='158'%20font-family='sans-serif'%20font-size='30'%20font-weight='700'%20fill='white'%20text-anchor='middle'%3EShorts%20Studio%3C/text%3E%3C/svg%3E",
      kind: "shorts-studio",
    },
  },
  {
    // Modo "Animar imagen" — pantalla propia (ruta /animar-imagen); Modos.tsx
    // navega ahí por `route`, igual que Shorts Studio. Convierte una foto (subida
    // o generada) en video: hasta ahora era un botón escondido adentro del
    // historial y del visor inline. Ver ADR-2026-08-14-modo-animar-imagen.
    //
    // Carátula PLACEHOLDER: reusa el clip explicativo VERTICAL (4:5) del panel de
    // Animar, así que en el slot 3:2 de /modos se recorta. Falta una landscape propia.
    path: "animar-imagen",
    route: "/animar-imagen",
    video: "/images/video_animar_imagen.mp4",
    // El poster NO es cosmético: es lo que corta el preload —
    // ADR-2026-08-20-el-video-de-animar-era-el-unico-que-preloadeaba.
    //
    // La tira de la home dibuja `preload={modo.poster ? 'none' : 'metadata'}`
    // (`EstiloMinimal.tsx`), o sea que la regla se invertía justo contra el archivo más
    // pesado: éste era el ÚNICO modo con `video` y sin `poster`, así que era el único
    // que arrancaba pidiendo bytes al montar, mientras los dos videos que sí tienen
    // poster iban en `preload="none"`.
    //
    // De paso resuelve el otro problema que tenía: sin poster, si el video no decodifica
    // la card quedaba gris y sin nada que mostrar.
    poster: "/images/video_animar_imagen_poster.jpg",
    preset: {
      id: "animar-imagen-card",
      title: "Animar imagen",
      description:
        "Convertí cualquier foto en un video con movimiento realista.",
      imageUrl: "/images/modo_video_ugc/modo_video_ugc_miniatura.png",
      imageUrlFull: "/images/modo_video_ugc/modo_video_ugc_miniatura.png",
      kind: "animar-imagen",
      outputType: "video",
      creditosPorUnidad: 1,
      unidadConsumo: "segundo",
    },
  },
  {
    // Modo "Carrusel" — pantalla propia (ruta /carrusel); Modos.tsx navega ahí por
    // `route`, igual que Animar imagen. Genera 2-6 tomas nuevas del mismo producto
    // a partir de una foto (subida o generada): hasta ahora era un botón escondido
    // adentro del historial y del visor inline. Ver ADR-2026-08-14-modo-carrusel.
    //
    // Sin `video` propio → Modos.tsx y HomeCards caen a `<img>`. La carátula es
    // el abanico de `/images/carrusel.png` centrado en un lienzo 1:1 con fondo:
    // el original es 1.75:1 con alpha y se comía los bordes en los slots 3:2
    // (desktop) y 4:5 (mobile), que recortan por ejes opuestos.
    path: "carrusel",
    route: "/carrusel",
    preset: {
      id: "carrusel-card",
      title: "Carrusel de imágenes",
      description:
        "De una foto, varias: nuevas tomas del mismo producto con otros ángulos y encuadres.",
      imageUrl: "/images/modo_carrusel/caratula.jpg",
      imageUrlFull: "/images/modo_carrusel/caratula.jpg",
      kind: "carrusel",
      creditosPorUnidad: 1,
      unidadConsumo: "imagen",
    },
  },
  {
    // Automática va con carátula FIJA — ADR-2026-08-05-automatica-va-con-foto-y-sin-video.
    // Sin `video`: los consumidores caen a `<img>` con el `poster`, igual que Anuncio,
    // catálogo-muebles e infografía. Era el ÚLTIMO modo con carátula en video (el
    // holograma), y encima tenía dos —éste y el "nuevo" que le pisaba el carrusel del
    // shell—, así que se veía distinto según la pantalla.
    //
    // La de acá es la carátula GENÉRICA, la que ve cualquier rubro. Hogar y Decoración
    // tiene la suya en `PORTADA_POR_RUBRO`.
    path: "auto",
    poster: "/images/modo_automatico/modo_auto_portada.jpg",
    preset: {
      id: "imagen-auto-card",
      title: "Generar imagen automáticamente",
      description:
        "Sumá tu producto y la IA arma todo: fondo, escena y luz por vos.",
      // La misma foto que el `poster`, por lo mismo que en Anuncio: si la card usa una y
      // la tira otra, el modo cambia de cara según quién lo pinte.
      imageUrl: "/images/modo_automatico/modo_auto_portada.jpg",
      imageUrlFull: "/images/modo_automatico/modo_auto.png",
      kind: "design-auto",
      creditosPorUnidad: 1,
      unidadConsumo: "imagen",
    },
  },
  {
    // Portada ÚNICA y estática (ADR-2026-08-04-modelo-portada-unica): el collage
    // "producto + fondo + cara → resultado" reemplaza a la carátula en video y a
    // los dos assets estáticos que había (`fot_modelo.png` como thumbnail y
    // `modelo_gia.png` como hero del detail sheet).
    //
    // Sin `video` a propósito — no es un olvido: todos los consumidores
    // (`/modos`, HomeCards, SheetModosProducto, EstiloMinimal) ya caen a `<img>`
    // cuando el modo no trae video, igual que catálogo-muebles e infografía.
    //
    // `poster` queda apuntando a la misma imagen porque `useModosGenerar` arma la
    // tira del inicio con `arte?.poster || m.poster || m.preset.imageUrl` — es el
    // canal por el que la portada llega ahí.
    path: "modelo",
    poster: "/images/modo_con_modelo/modo_modelo_portada.jpg",
    preset: {
      id: "imagen-con-modelo",
      title: "Generar imagen personalizada",
      description:
        "Tu producto sobre un modelo IA en la pose y el fondo que vos elijas.",
      imageUrl: "/images/modo_con_modelo/modo_modelo_portada.jpg",
      // El hero del detail sheet ("Inicio / Modelo") se queda con el asset de
      // siempre: ahí la portada nueva no aporta —la pantalla YA explica el flujo
      // con su bajada— y encima repetía el mismo collage dos veces seguidas
      // (card → detalle). La portada nueva vive en la carátula, no acá.
      imageUrlFull: "/images/modo_con_modelo/modelo_gia.png",
      kind: "design-modelo",
      creditosPorUnidad: 1,
      unidadConsumo: "imagen",
    },
    // El producto va sobre un modelo IA: no aplica a muebles.
    ocultoEnHogarDeco: true,
  },
  {
    // Modo "Fotos de producto" (ADR-2026-08-13-modo-fotos-producto). El producto
    // SOLO, sin modelo: se elige una o varias vistas (lateral, plano frente,
    // maniquí invisible…) y el fondo (blanco o escena descrita). Va pegado a
    // "Imagen personalizada" porque es su hermano sin modelo.
    //
    // Card SIN video propio todavía: muestra `imageUrl` estático (PLACEHOLDER —
    // reusa un thumb del catálogo de vistas hasta tener carátula propia).
    // Duplicada con la card sintética de InterfazV2 (patrón de todos los modos):
    // si tocás una, tocá la otra.
    path: "fotos-producto",
    preset: {
      id: "fotos-producto-card",
      title: "Fotos de producto",
      description:
        "Tu producto solo, en las vistas que necesites: fondo blanco o la escena que describas.",
      // `imageUrl` = carátula de la card (PLACEHOLDER, ver arriba).
      // `imageUrlFull` = hero de "Elegí el producto": el collage antes → después
      // (prenda en percha → foto limpia) que muestra qué hace el modo.
      imageUrl: "/images/multi-vistas/zapatillas/lateral.webp",
      imageUrlFull: "/images/modo_fotos_producto/hero_producto.png",
      kind: "fotos-producto",
      creditosPorUnidad: 1,
      unidadConsumo: "imagen",
    },
  },
  {
    path: "catalogo",
    video: "/images/modo_catalogo_ia/modo_catalogo_caratula_desktop.mp4",
    poster: "/images/modo_catalogo_ia/modo_catalogo_hero.png",
    preset: {
      id: "catalogo-ia-card",
      title: "Generar catálogo con IA",
      description:
        "Photoshoots por producto con varias vistas y poses en un solo proceso.",
      imageUrl: "/images/modo_con_modelo/fot_modelo.png",
      imageUrlFull: "/images/modo_catalogo_ia/modo_catalogo_hero.png",
      kind: "catalogo-ia",
      creditosPorUnidad: 1,
      unidadConsumo: "imagen",
    },
    // Su reemplazo para hogar/deco es el catálogo de muebles, que se muestra con
    // este mismo título — ADR-2026-08-01-hogar-deco-solo-catalogo-muebles-renombrado.
    ocultoEnHogarDeco: true,
  },
  {
    // Modo "Catálogo de muebles" (ADR-582). Visible solo para marcas del rubro
    // "Hogar y Decoración" (`soloHogarDeco`) — los consumidores de MODES tienen
    // que filtrarlo, igual que con `devOnly`.
    //
    // Card SIN video propio: muestra `imageUrl` estático (el ropero en contexto,
    // ADR-817). Sin `video`/`poster` → Modos.tsx y HomeCards caen a `<img>`. El
    // `imageUrlFull` (hero de "Elegí el producto") lo reemplaza el collage
    // `ProductHeroCollage`, así que el que quede acá no se ve.
    path: "catalogo-muebles",
    preset: {
      id: "catalogo-muebles-card",
      // Se llama igual que el Catálogo IA a propósito: para una marca de Hogar y
      // Decoración ESTE es su catálogo con IA (el otro modo no se le muestra), así
      // que el nombre "de muebles" era una distinción interna que no le decía nada
      // — ADR-2026-08-01-hogar-deco-solo-catalogo-muebles-renombrado.
      title: "Generar catálogo con IA",
      description:
        "Fondo blanco, infografía con medidas y foto en contexto, para cada uno de tus productos.",
      imageUrl: "/images/modo_catalogo_muebles/hero_contexto.jpg",
      imageUrlFull: "/images/modo_catalogo_ia/modo_catalogo_hero.png",
      kind: "catalogo-muebles",
      creditosPorUnidad: 1,
      unidadConsumo: "imagen",
    },
    soloHogarDeco: true,
  },
  {
    // Modo "Infografía" (ADR-813): fichas técnicas dimensionales de los productos.
    // Reusa el flujo y el motor del catálogo de muebles pero salteando el picker de
    // vistas (su única vista es la infografía). Mismo gate de rubro de marca
    // que el catálogo de muebles (`soloHogarDeco`).
    // Card SIN video propio: muestra `imageUrl` estático (una infografía con cotas,
    // ADR-817); el hero de "Elegí el producto" lo pone ProductHeroCollage.
    path: "infografia",
    preset: {
      id: "infografia-card",
      title: "Infografías de producto con IA",
      description:
        "Fichas técnicas con las medidas y características de cada uno de tus productos.",
      imageUrl: "/images/modo_infografia/hero_foto.jpg",
      imageUrlFull: "/images/modo_catalogo_ia/modo_catalogo_hero.png",
      kind: "infografia",
      creditosPorUnidad: 1,
      unidadConsumo: "imagen",
    },
    soloHogarDeco: true,
  },
  {
    // Modo "Video con influencer" — visible para TODOS los planes desde
    // ADR-561 (con candado + bloqueo de entrada para gratis/Básico vía
    // getModePlanLock, ADR-564). Carátula en video LANDSCAPE propia (16:9,
    // para el slot 3:2 de la página Modos) + su poster. El hero MOBILE usa
    // otro asset vertical (720x960). La miniatura (portrait) es la imagen
    // "al lado de la explicación". Ver ADR-506 / ADR-509.
    path: "video-influencer",
    video: "/images/modo_video_ugc/modo_video_ugc_caratula_desktop.mp4",
    poster: "/images/modo_video_ugc/modo_video_ugc_caratula_desktop.jpg",
    preset: {
      id: "video-influencer-card",
      title: "Video con influencer",
      description:
        "Generá un video de tu producto presentado por un influencer IA, con un gancho que enganche en los primeros segundos.",
      imageUrl: "/images/modo_video_ugc/modo_video_ugc_miniatura.png",
      imageUrlFull: "/images/modo_video_ugc/modo_video_ugc_miniatura.png",
      poster: "/images/modo_video_ugc/modo_video_ugc_caratula_desktop.jpg",
      kind: "video-influencer",
      outputType: "video",
      creditosPorUnidad: 1,
      unidadConsumo: "segundo",
    },
  },
  {
    path: "asistente",
    video: "/images/modo_asistente/modo_asistente_caratula_desktop.mp4",
    // Poster del clip VIVO — ADR-2026-08-20-la-caratula-del-asistente-era-de-un-clip-deprecado.
    // Acá apuntaba a `modo_asistente_caratula_mobile.jpg`, que es un frame de
    // `modo_asistente_caratula_mobile_v1.mp4` (deprecado): TRES MUJERES CLONADAS CON
    // HOLOGRAMAS. Para un modo de CHAT ("Crear desde cero") eso se lee como un modo de
    // modelos IA. Ahora es un frame del mismo clip que declara `video` acá arriba: la
    // barra de prompt escribiendo, que es lo que el modo hace.
    poster: "/images/modo_asistente/modo_asistente_poster.jpg",
    preset: {
      id: "asistente-marketing-card",
      title: "Crear desde cero",
      description: "Generá, analizá y planificá tu contenido",
      imageUrl: "/images/modo_con_modelo/fot_modelo.png",
      imageUrlFull: "/images/modo_catalogo_ia/modo_catalogo_hero.png",
      kind: "asistente",
      creditosPorUnidad: 1,
      unidadConsumo: "imagen",
    },
  },
  {
    // Anuncio va con carátula FIJA — ADR-2026-08-04-anuncio-va-con-foto-y-sin-video.
    // Sin `video`: los consumidores caen a `<img>` con el `poster`, igual que
    // catálogo-muebles e infografía. Antes tenía dos videos (éste y el "nuevo" que le
    // pisaba el carrusel del shell), así que el modo se veía distinto según la
    // pantalla; ahora es una sola imagen en todos lados.
    //
    // La de acá es la carátula GENÉRICA, la que ve cualquier rubro. La del aviso de la
    // mesa de luz —que era ésta hasta ADR-2026-08-05-la-portada-de-anuncio-es-de-hogar—
    // vive ahora en `PORTADA_POR_RUBRO`, para Hogar y Decoración.
    path: "anuncio",
    // Fuera de las listas de descubrimiento —
    // ADR-2026-08-20-anuncio-sale-de-las-listas-sin-salir-del-catalogo. La entrada NO
    // se borra: se siguen llegando a ella el atajo de "Ediciones rápidas", los botones
    // del visor y del historial (que hacen `MODES.find(m => m.path === 'anuncio')`) y
    // el deep-link `/interfaz-v2/modo/anuncio`.
    ocultoEnListas: true,
    poster: "/images/modo_anuncio/modo_anuncio_portada.jpg",
    preset: {
      id: "anuncio-card",
      title: "Anuncio",
      description:
        "Texto, colores y formato — armá un anuncio listo para publicar.",
      // La misma foto que el `poster`: era la miniatura de modelo prestada, así que
      // la carátula del modo cambiaba según quién la pintara
      // (ADR-2026-08-04-anuncio-va-con-foto-y-sin-video).
      imageUrl: "/images/modo_anuncio/modo_anuncio_portada.jpg",
      imageUrlFull: "/images/modo_anuncio/modo_anuncio_hero.png",
      kind: "anuncio",
      creditosPorUnidad: 1,
      unidadConsumo: "imagen",
    },
  },
];

/**
 * Portadas para cajas CUADRADAS — ADR-2026-08-04-la-portada-cuadrada-no-es-de-mobile,
 * que supersede a ADR-2026-08-04-modelo-tiene-portada-propia-en-mobile.
 *
 * Un modo tiene una sola carátula (ADR-2026-08-04-el-carrusel-de-mobile-usa-las-caratulas-de-desktop),
 * y esta tabla es la excepción explícita: la carátula es apaisada y hay superficies
 * donde la caja es 1:1 o casi, así que el recorte central se come el encuadre.
 *
 * Nació como "portadas de mobile" porque las dos primeras superficies con ese problema
 * —el hero del inicio y las cards del sheet de Generar— son de teléfono. Pero lo que la
 * define no es el dispositivo sino la FORMA de la caja: el drawer "¿Qué querés generar?"
 * de Mi marca tiene cards `aspect-square` y se ve igual en desktop. Atarla a mobile
 * dejaba a esa pantalla mostrando el recorte apaisado sin ninguna razón.
 *
 * Indexada por `path` (la misma llave que `CARRUSEL_MODOS`) y no por `kind`, para que
 * quien la consulte use la conversión que ya tiene a mano. Un modo sin entrada acá
 * sigue con su carátula única, que es lo normal.
 *
 * El criterio para sumar un consumidor es la caja, no la pantalla: si dibuja la carátula
 * en 1:1 (o muy cerca), va; si la dibuja apaisada, usa la de siempre.
 */
/**
 * Carátulas que dependen del RUBRO de la marca activa —
 * ADR-2026-08-05-la-caratula-de-catalogo-sigue-al-rubro.
 *
 * La carátula de un modo muestra lo que el modo produce, y "lo que produce" cambia con
 * el rubro: la de Catálogo es una grilla de vistas de un traje sobre una modelo, que a
 * una marca de muebles no le dice nada de un modo que le va a fotografiar roperos.
 *
 * Indexada por rubro → `path`, las dos llaves que el llamador ya tiene a mano. Un modo
 * sin entrada para el rubro activo se queda con su carátula de siempre, que es el caso
 * normal: esta tabla es la excepción, no el mecanismo.
 *
 * Cada entrada trae la APAISADA (3:2 — la tira de la home, `/modos`) y, si el modo la
 * necesita, la CUADRADA (1:1 — hero de mobile, cards del sheet de Generar y del drawer
 * de Mi marca). Sin `cuadrada`, la apaisada sirve para las dos: es lo que pasa con
 * Catálogo, cuyo asset es vertical y con el mueble centrado, así que ningún recorte lo
 * corta. Anuncio sí tiene las dos, porque su aviso trae texto y en 1:1 hay que
 * reencuadrarlo a mano.
 */
export const PORTADA_POR_RUBRO: Record<
  string,
  Record<string, { apaisada: string; cuadrada?: string }>
> = {
  [RUBRO_HOGAR_DECO]: {
    // El ropero en contexto — el mismo asset que ya usa la card del modo "Catálogo de
    // muebles" (ADR-817), que es el catálogo de este rubro contado en una foto. El
    // rubro es "Hogar y Decoración" y no "Muebles" (ADR-2026-08-05-infografia-la-ve-el-rubro-hogar-y-decoracion):
    // son dos rubros distintos en la app y las marcas del caso están en el primero.
    catalogo: { apaisada: '/images/modo_catalogo_muebles/hero_contexto.jpg' },
    // Automática con portada propia del rubro. OJO: acá el modo pierde su carátula en
    // VIDEO (el holograma), porque una portada de rubro siempre se pinta como imagen
    // fija — ver el comentario de arriba.
    auto: {
      apaisada: '/images/modo_automatico/modo_auto_portada_hogar.jpg',
      cuadrada: '/images/modo_automatico/modo_auto_portada_hogar_cuadrada.jpg',
    },
    // El aviso de la mesa de luz — ADR-2026-08-05-la-portada-de-anuncio-es-de-hogar.
    // Era la carátula de Anuncio para TODOS los rubros: un aviso de mueble con su copy
    // ("Tu rincón de descanso perfecto") ofreciéndose como ejemplo de lo que el modo
    // hace, a marcas de moda, gastronomía o cosmética. Acá pasa a ser lo que siempre
    // fue: la portada de este rubro.
    anuncio: {
      apaisada: '/images/modo_anuncio/mueble_cama.jpg',
      cuadrada: '/images/modo_anuncio/mueble_cama_cuadrada.jpg',
    },
  },
};

/** La carátula APAISADA propia del rubro para ese modo, o `undefined` si no tiene. */
export const portadaDeRubro = (
  path: string,
  rubro?: string | null,
): string | undefined =>
  rubro ? PORTADA_POR_RUBRO[rubro]?.[path]?.apaisada : undefined;

/**
 * La CUADRADA del rubro, con la apaisada de respaldo: un rubro que declara portada
 * propia tiene que ganarle a `PORTADA_CUADRADA` en las dos formas de caja, o la card
 * cuadrada se quedaría mostrando la portada genérica del modo.
 */
export const portadaCuadradaDeRubro = (
  path: string,
  rubro?: string | null,
): string | undefined => {
  const entrada = rubro ? PORTADA_POR_RUBRO[rubro]?.[path] : undefined;
  return entrada?.cuadrada ?? entrada?.apaisada;
};

export const PORTADA_CUADRADA: Record<string, string> = {
  auto: '/images/modo_automatico/modo_auto_portada_cuadrada.jpg',
  modelo: '/images/modo_con_modelo/modo_modelo_portada_mobile.jpg',
  // El nombre del archivo de Modelo dice `_mobile` por la primera versión de esta
  // tabla; el de Anuncio ya nace con el nombre correcto (`_cuadrada`). No se renombra
  // el viejo para no tocar un asset por cosmética.
  anuncio: '/images/modo_anuncio/modo_anuncio_portada_cuadrada.jpg',
  // El asistente entra acá con ADR-2026-08-20-la-caratula-del-asistente-era-de-un-clip-deprecado:
  // su carátula es apaisada (1080x716) y en las cajas 1:1 —el drawer de un producto, el
  // hero de mobile— el `object-cover` se comía los extremos de la barra de prompt, que
  // es justo lo único que identifica al modo. El recorte centrado a 716x716 la conserva
  // entera.
  asistente: '/images/modo_asistente/modo_asistente_poster_cuadrada.jpg',
};

/**
 * Anuncio, tercero — ADR-2026-08-03-anuncio-tercero-tira-y-sidebar.
 *
 * En el orden natural de `MODES` cae ÚLTIMO, detrás de los dos modos de catálogo y
 * del asistente, y es de las cosas que más se usan. En vez de reordenar `MODES` —que
 * lo consumen `/modos`, la home de V2 y la fila de íconos del estilo Workspace, cada
 * una con sus propias razones de orden— se reubica en el punto de uso: la tira de la
 * home y la sidebar del shell, que son las dos listas donde el usuario lo busca.
 *
 * Genérico y con accesor: las dos listas tienen forma distinta (`HerramientaAtlas`
 * usa `id`; los modos de la tira, `path`). Si Anuncio no está —filtrado por rubro o
 * por feature flag— devuelve la lista tal cual.
 */
export function anuncioTercero<T>(
  items: T[],
  claveDe: (item: T) => string | null | undefined,
): T[] {
  const i = items.findIndex((item) => claveDe(item) === 'anuncio');
  // Tercero = índice 2. Con menos de tres ítems, `splice` lo deja al final, que es
  // lo más cerca del pedido que se puede estar.
  if (i === -1 || i === 2) return items;
  const copia = [...items];
  const [anuncio] = copia.splice(i, 1);
  copia.splice(2, 0, anuncio);
  return copia;
}

/**
 * LOS MODOS DEL HERO DE LA HOME DE MOBILE EN LA PRUEBA GRATIS —
 * ADR-2026-08-20-la-home-gratis-de-hogar-muestra-automatico-y-fotos.
 *
 * En la prueba gratis, el hero de la home de mobile (`MobileModeHero`) no lista todos
 * los modos sino una selección corta; el resto sigue accesible desde `/modos`, que no se
 * filtra. Esa selección era una sola, escrita a mano en `InterfazV2`, y estaba pensada
 * para una marca de ropa: `design-auto` + `design-modelo` + `asistente`.
 *
 * Para **Hogar y Decoración** eso no funcionaba. `design-modelo` tiene
 * `ocultoEnHogarDeco` —pone el producto sobre un modelo IA, que no aplica a un mueble—,
 * así que de los tres elegidos ese rubro veía DOS, y uno de ellos era el chat. La
 * vidriera de la prueba les mostraba lo que menos les sirve.
 *
 * Ahora son dos listas:
 *   • Hogar y Decoración → `design-auto` + `fotos-producto`.
 *   • el resto           → `design-auto` + `design-modelo` + `asistente`, como estaba.
 *
 * Razona por `kind` y no por `path` porque el hero consume los presets de `design-row`,
 * una lista que `InterfazV2` arma en su render y que sólo tiene `kind`.
 *
 * OJO: es un filtro APARTE del gating por plan. Abrir un modo en `getModePlanLock` no
 * alcanza para que aparezca acá —le pasó a `asistente` en
 * ADR-2026-08-20-crear-desde-cero-se-abre-y-el-candado-baja-a-las-opciones, que quedó
 * abierto pero invisible en la home de mobile justo para el plan al que se le abrió—.
 * Si un modo tiene que estar en la vidriera de la prueba, va agregado acá.
 */
export function kindsHomeGratis(esHogarDeco: boolean): Set<string> {
  return esHogarDeco
    ? new Set(['design-auto', 'fotos-producto'])
    : new Set(['design-auto', 'design-modelo', 'asistente']);
}

/**
 * EL MODO QUE VA EN GRANDE en "Generar → Imágenes" de mobile —
 * ADR-2026-08-20-el-modo-en-grande-de-generar-sigue-al-rubro.
 *
 * Ese paso dibuja una card destacada (3:2) arriba y el resto en una grilla de a dos. La
 * destacada era el `path` fijo `'modelo'`, escrito en `SheetGenerar`, y para las marcas
 * de **Hogar y Decoración** eso no destacaba nada: `modelo` tiene `ocultoEnHogarDeco`,
 * así que el `find` no lo encontraba y los seis modos caían iguales a la grilla — la
 * pantalla se quedaba sin jerarquía justo para ese rubro.
 *
 * Ahora la elige el rubro, que es de donde ya salen las otras dos decisiones de esta
 * lista (qué modos entran y con qué carátula se ven):
 *
 *   • Hogar y Decoración → `catalogo-muebles`, que es SU "Generar catálogo con IA"
 *     (ADR-2026-08-01-hogar-deco-solo-catalogo-muebles-renombrado).
 *   • el resto           → `modelo`, como estaba.
 *
 * Devuelve un `path` y no un índice, igual que antes: si el modo se cae por rubro, por
 * plan o por feature flag, el `find` no lo encuentra y la pantalla sigue en pie sin
 * destacada, que es el comportamiento que ya había.
 */
export function modoDestacadoGenerar(esHogarDeco: boolean): string {
  return esHogarDeco ? 'catalogo-muebles' : 'modelo';
}

/**
 * Carrusel al final — ADR-2026-08-20-el-modo-en-grande-de-generar-sigue-al-rubro.
 *
 * El gemelo de `anuncioTercero`, y por el mismo motivo: se reubica en el PUNTO DE USO en
 * vez de reordenar `MODES`, que lo consumen `/modos`, la tira del inicio y la sidebar,
 * cada una con sus propias razones de orden.
 *
 * En el orden natural del catálogo, Carrusel cae ANTES que todo lo demás en la familia
 * de imágenes, así que para una marca de Hogar y Decoración era la primera card de la
 * grilla siendo el modo menos importante del rubro.
 *
 * Genérico y con accesor por la misma razón que `anuncioTercero`: las listas que lo
 * usan tienen forma distinta. Si Carrusel no está —filtrado por sujeto, por rubro o por
 * feature flag— devuelve la lista tal cual.
 */
export function carruselUltimo<T>(
  items: T[],
  claveDe: (item: T) => string | null | undefined,
): T[] {
  const i = items.findIndex((item) => claveDe(item) === 'carrusel');
  if (i === -1 || i === items.length - 1) return items;
  const copia = [...items];
  const [carrusel] = copia.splice(i, 1);
  copia.push(carrusel);
  return copia;
}

/**
 * Path de modo → `kind` del preset. El inverso del `MODE_KIND_TO_PATH` que vive en
 * `InterfazV2`, derivado de `MODES` en vez de escrito a mano para que no puedan
 * discrepar.
 *
 * Lo necesita quien tiene el PATH y quiere preguntar por el plan: `getModePlanLock`
 * razona sobre `kind`, y la sidebar del shell lista rutas (ADR-2026-08-03-sidebar-candado-modos-sin-plan).
 */
export const MODE_PATH_TO_KIND: Record<string, string> = Object.fromEntries(
  MODES.map((m) => [m.path, m.preset.kind]).filter(
    (par): par is [string, string] => typeof par[1] === 'string',
  ),
);

/**
 * `kind` del preset → path del modo. El inverso del de arriba, y también derivado de
 * `MODES` — ADR-2026-08-04-nombres-de-modo-iguales-en-mobile.
 *
 * Estaba escrito a mano en `InterfazV2` (idéntico, hasta que alguien agregara un modo
 * en un lado y no en el otro). Lo necesita todo el que tiene un preset y quiere el
 * NOMBRE del modo: los nombres viven en `COPY_TIRA`, indexados por path.
 */
export const MODE_KIND_TO_PATH: Record<string, string> = Object.fromEntries(
  MODES.map((m) => [m.preset.kind, m.path]).filter(
    (par): par is [string, string] => typeof par[0] === 'string',
  ),
);

/**
 * EL CATÁLOGO DEL RUBRO — ADR-2026-08-20-el-slug-catalogo-no-tiene-card-en-hogar-y-deco.
 *
 * "Generar catálogo con IA" son DOS `kind` distintos según el rubro de la marca activa:
 * `catalogo-muebles` para Hogar y Decoración y `catalogo-ia` para todos los demás. Cada
 * uno está OCULTO para el rubro del otro (`soloHogarDeco` / `ocultoEnHogarDeco`), y en
 * `InterfazV2` eso se traduce en que su card sintética sale `null`.
 *
 * El problema que resuelve: hay entradas que llegan al modo con el slug ESCRITO A MANO
 * —el tab "Catálogo" de la barra inferior de mobile (`NavInferiorMobile`), el botón
 * "Nuevo catálogo" del estilo Workspace, y cualquier URL compartida—. Ese slug fijo es
 * `catalogo`, o sea `catalogo-ia`, o sea justo el kind que Hogar y Decoración NO tiene:
 * el efecto de ruta se encuentra la card en `null`, sale por `if (!card) return` —una
 * rama que existe para ESPERAR a que el rubro resuelva, ADR-2026-08-05-el-deep-link-de-un-modo-espera-a-su-rubro—
 * y la espera no termina nunca. El tab queda activo y no monta nada.
 *
 * Se resuelve acá y no en cada entrada a propósito: los llamadores hardcodean el slug
 * porque NO tienen por qué saber de rubros —la barra inferior sólo sabe si la marca es
 * de Moda—, y parchear uno por uno deja afuera al siguiente que se escriba y a los
 * deep-links, que no tienen a quién parchear.
 *
 * Es SIMÉTRICO: el caso espejo —una marca que no es de Hogar y Deco entrando a
 * `/interfaz-v2/modo/catalogo-muebles` por un link compartido o por cambiar de marca con
 * la URL abierta— se cuelga por el mismo motivo. Las dos direcciones son "mostrame el
 * catálogo" y la respuesta correcta es el catálogo que esa marca tiene.
 *
 * `infografia` NO entra en el swap, y es deliberado: no es "el catálogo del otro rubro"
 * sino un modo que fuera de Hogar y Decoración no existe. Mandarlo a `catalogo-ia`
 * abriría un modo que el usuario no pidió; que ahí no pase nada es correcto.
 */
export function kindDeModoParaRubro<T extends string | null | undefined>(
  kind: T,
  esHogarDeco: boolean,
): T {
  if (kind === 'catalogo-ia' && esHogarDeco) return 'catalogo-muebles' as T;
  if (kind === 'catalogo-muebles' && !esHogarDeco) return 'catalogo-ia' as T;
  return kind;
}

/**
 * Modos en los que se puede ELEGIR un modelo —
 * ADR-2026-08-04-desde-un-modelo-se-elige-el-modo.
 *
 * Lo pregunta el sheet de modos cuando se abre desde un modelo de "Marca → Modelos":
 * ofrecer ahí un modo que no tiene picker de modelo sería llevar al usuario a una
 * pantalla donde lo que eligió no aparece por ningún lado.
 *
 * Se razona por `kind` y no por `path` porque es el `kind` el que decide el flow:
 *
 *   • `design-modelo`    → `DesignModeloPanel` con el picker de Modelo.
 *   • `catalogo-ia`      → `CatalogoCardsFullscreen`, que tiene botón de Modelo
 *                          cuando la tanda incluye poses con modelo (las de default).
 *   • `video-influencer` → la sección "Modelo" del panel de influencer, donde además
 *                          es OBLIGATORIO elegirlo.
 *
 * Quedan afuera, y a propósito: `design-auto` (el panel corre con
 * `hideAutoPickers`), `anuncio` (`AnuncioPanel`: textos, colores y formato),
 * `catalogo-muebles` e `infografia` (sus vistas son solo-producto) y `asistente`
 * (el modelo entra como adjunto del chat, que no se puede precargar por el canal de
 * restore).
 */
const KINDS_CON_MODELO = new Set([
  'design-modelo',
  'catalogo-ia',
  'video-influencer',
]);

export function modoAceptaModelo(kind: string | null | undefined): boolean {
  return !!kind && KINDS_CON_MODELO.has(kind);
}

/**
 * Modos en los que se puede partir de un PRODUCTO del catálogo —
 * ADR-2026-08-20-desde-un-producto-tambien-hay-modos-que-no-aplican.
 *
 * El gemelo de `KINDS_CON_MODELO`, para el otro sujeto del sheet. Existía el eje
 * "abierto desde un modelo" y NO existía el de producto, así que desde "Marca →
 * Productos" se ofrecían modos que ni siquiera miran el producto elegido: se tocaba una
 * remera y se caía en "elegí una imagen generada", donde esa remera no está.
 *
 * **Esta lista no es una opinión: es la de `InterfazV2.tsx`.** Son exactamente los
 * `kind` que su initializer de paso manda a `choose-product` / `select-vistas` /
 * `configure` cuando le llega `initialNavProducts`. Si mañana un modo empieza a aceptar
 * producto allá, tiene que entrar acá — y al revés.
 *
 * Quedan afuera, y por qué:
 *   • `carrusel` y `animar-imagen` → arrancan en `GeneradasPickGrid`, que lista
 *     `imagenes_generadas`. Ni siquiera leen `location.state`: un producto del catálogo
 *     no es una fila de esa tabla.
 *   • `shorts-studio` → parte de un video propio que el usuario sube.
 *   • `asistente` → `InterfazV2` corta con `if (k === 'asistente') return 'asistente'`
 *     ANTES de mirar `initialNavProducts`, y `AsistenteMarketingSheet` nunca recibe los
 *     productos: el chat los toma como adjunto, que este canal no precarga. Es el mismo
 *     motivo por el que `KINDS_CON_MODELO` lo deja afuera.
 */
const KINDS_CON_PRODUCTO = new Set([
  'design-auto',
  'design-modelo',
  'fotos-producto',
  'catalogo-ia',
  'catalogo-muebles',
  'infografia',
  'anuncio',
  'video-influencer',
]);

export function modoAceptaProducto(kind: string | null | undefined): boolean {
  return !!kind && KINDS_CON_PRODUCTO.has(kind);
}

/**
 * Contexto con el que se decide qué modos ve alguien.
 *
 * Todos los campos son opcionales y el default es el CASO ABIERTO (nada filtrado por
 * ese eje), salvo `isDevTester`, cuyo default es `false` — un modo interno no se
 * muestra "por las dudas".
 */
export interface ContextoModosVisibles {
  /** `company_profiles.dev_tester`. Sólo `true` abre los modos `devOnly`. */
  isDevTester?: boolean | null;
  /** `true` si la marca activa es del rubro "Hogar y Decoración". */
  esHogarDeco?: boolean;
  /**
   * `true` si la lista se está abriendo DESDE un modelo. Recorta a los modos donde el
   * modelo se puede elegir (ADR-2026-08-04-desde-un-modelo-se-elige-el-modo). Lo usan
   * el sheet de Generar y el drawer de Mi marca; `/modos` y la sidebar no.
   */
  esModelo?: boolean;
  /**
   * `true` si la lista se está abriendo DESDE un producto del catálogo. El gemelo del
   * de arriba — ADR-2026-08-20-desde-un-producto-tambien-hay-modos-que-no-aplican.
   *
   * Ojo con el default `false`: las superficies SIN sujeto (`/modos`, la tira del
   * inicio, la sidebar, el sheet de Generar de la barra inferior) tienen que seguir
   * ofreciendo Animar y Carrusel, porque ahí no hay ningún producto que contradecir.
   */
  esProducto?: boolean;
}

/**
 * QUIÉN VE QUÉ MODOS. Fuente de verdad única —
 * ADR-2026-08-20-el-filtro-de-modos-estaba-copiado-en-cuatro-lugares.
 *
 * Esta misma lista de condiciones estaba escrita CUATRO veces —`Modos.tsx`,
 * `useModosGenerar.ts`, `SheetModosProducto.tsx` y el filtro de la sidebar en
 * `EstiloMinimal.tsx`— y ya habían divergido: las tres primeras chequeaban `devOnly` y
 * la sidebar no, así que un modo interno salía de `/modos` y del sheet pero seguía
 * listado en "Todas las herramientas". Agregar un eje nuevo significaba acordarse de
 * cuatro archivos, y el cuarto era justo el que nadie tocaba.
 *
 * Pura y exportada para poder testearla. El orden de `MODES` se respeta.
 *
 * OJO con la sidebar: no consume `MODES` sino `HERRAMIENTAS` de `contenidoAtlas.ts`,
 * que se arma con un `.map()` sobre `MODES`. Ese map tiene que COPIAR los flags o acá
 * llegan todos en `undefined` y la función no filtra nada.
 */
/** Los cuatro flags de visibilidad que un modo declara en el catálogo. */
export interface FlagsVisibilidadModo {
  devOnly?: boolean;
  ocultoEnListas?: boolean;
  soloHogarDeco?: boolean;
  ocultoEnHogarDeco?: boolean;
}

/**
 * El predicado, sobre los FLAGS solos.
 *
 * Existe aparte de `modosVisibles` porque hay un consumidor que no lista `ModeCard`
 * sino `HerramientaAtlas` (la sidebar, vía `contenidoAtlas`), y forzarlo a mapear de
 * ida y vuelta para poder filtrar era peor que compartir el predicado. Ese consumidor
 * venía chequeando los mismos ejes A MANO y por `id` —`if (h.id === 'infografia')
 * return esRubroHogarDeco(...)`—, o sea una quinta copia escrita de otra forma.
 */
export function modoVisiblePorFlags(
  flags: FlagsVisibilidadModo,
  ctx: { isDevTester?: boolean | null; esHogarDeco?: boolean } = {},
): boolean {
  const { isDevTester = false, esHogarDeco = false } = ctx;
  // Fuera de las listas de descubrimiento, pero vivo en el catálogo: sigue alcanzable
  // por atajo, por acción sobre una imagen y por URL.
  if (flags.ocultoEnListas) return false;
  // Modos de muebles (catálogo + infografías): sólo marcas de Hogar y Decoración
  // (ADR-2026-07-31-modos-muebles-por-rubro-de-marca).
  if (flags.soloHogarDeco && !esHogarDeco) return false;
  // Inverso: los modos que ponen el producto sobre un modelo IA no aplican a un mueble
  // (ADR-2026-07-31-hogar-deco-sin-plantillas-ni-modelo).
  if (flags.ocultoEnHogarDeco && esHogarDeco) return false;
  // Modos internos: sólo dev testers.
  if (flags.devOnly && isDevTester !== true) return false;
  return true;
}

export function modosVisibles(
  modos: ModeCard[],
  ctx: ContextoModosVisibles = {},
): ModeCard[] {
  const { esModelo = false, esProducto = false } = ctx;
  return modos.filter((m) => {
    // "Video con influencer" oculto para todos mientras el flag esté off. Es un flag
    // global del módulo y no del modo, por eso no entra en `modoVisiblePorFlags`.
    if (m.path === 'video-influencer' && !VIDEO_INFLUENCER_ENABLED) return false;
    if (!modoVisiblePorFlags(m, ctx)) return false;
    // Abierto desde un modelo: los otros modos no se muestran apagados sino que no se
    // muestran — no es una restricción de plan que se levante pagando, es que ahí el
    // modelo no entra a ningún lado.
    if (esModelo && !modoAceptaModelo(m.preset.kind)) return false;
    // Ídem desde un producto: los modos que arrancan de una imagen ya generada no lo
    // miran, así que ofrecerlos es mandar al usuario a una pantalla donde lo que
    // acaba de elegir no existe.
    if (esProducto && !modoAceptaProducto(m.preset.kind)) return false;
    return true;
  });
}
